// The cooler outbox's delivery leg (v1.84.0): whenever the network comes
// back — or the app opens — queued notes send themselves, oldest first, on
// their original idempotency keys (a retry can never double-post). One
// global wiring point (Layout), one in-flight guard, and a DOM event so any
// open thread repaints its "waiting" rows the moment they deliver.
import { api } from '@/core/api/apiClient';
import { useAuthStore } from '@/features/auth/store/authStore';
import { useToastStore } from '@/core/ui/toastStore';
import { flushOutbox, listQueued } from './outbox';

let inFlight = false;

export async function flushQueuedComments(): Promise<void> {
  const uid = useAuthStore.getState().user?.uid;
  if (!uid || inFlight || listQueued(undefined, uid).length === 0 || !navigator.onLine) return;
  inFlight = true;
  try {
    const r = await flushOutbox(uid, async (q) => {
      await api.post(`/tasks/${q.taskId}/messages`, {
        body: q.bodyHtml, mentionUserIds: q.mentionUserIds, parentId: q.parentId,
        attachmentIds: [], channel: q.channel, clientKey: q.clientKey,
      });
    });
    if (r.sent.length > 0 || r.refused.length > 0) {
      window.dispatchEvent(new Event('cgp:outbox-changed'));
    }
    const toast = useToastStore.getState().show;
    if (r.sent.length > 0) {
      toast(r.sent.length === 1
        ? `Your note on “${r.sent[0]!.taskName || 'a card'}” was delivered.`
        : `${r.sent.length} waiting notes were delivered.`, 'info');
    }
    for (const q of r.refused) {
      toast(`A waiting note on “${q.taskName || 'a card'}” was refused and won’t retry — open the card to see why.`, 'error');
    }
  } finally {
    inFlight = false;
  }
}

/** Call once at app mount. */
export function startOutboxDelivery(): () => void {
  const onOnline = () => void flushQueuedComments();
  window.addEventListener('online', onOnline);
  // App open with signal = a delivery chance too (the phone found wifi while
  // the app was closed).
  void flushQueuedComments();
  return () => window.removeEventListener('online', onOnline);
}
